import React, { useEffect, useState } from "react";
import { FaCheck, FaInfoCircle, FaTimes } from "react-icons/fa";
import { FaReddit } from "react-icons/fa6";
import { toast } from "react-toastify";

// const API_BASE = "http://localhost:3000"; // ← change if testing locally
const API_BASE = "https://api.filetrust.io"; // change to production API host in prod
const SUBREDDIT_NAME = "FileTrust"; // without r/

const RedditTask = ({ user, reward, getData }) => {
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [connected, setConnected] = useState(false);
  const [me, setMe] = useState(null);

  // check connection to Reddit on load
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/api/reddit/me`, {
          credentials: "include",
        });
        const j = await r.json();
        if (j.ok) {
          setConnected(true);
          setMe(j.me);
        } else {
          setConnected(false);
          setMe(null);
        }
      } catch {
        setConnected(false);
        setMe(null);
      }
    })();
  }, []);

  const handleStartClick = () => {
    // open subreddit page for the user to join
    window.open(`https://www.reddit.com/r/${SUBREDDIT_NAME}/`, "_blank");
  };

  const handleOpenModal = () => setModalOpen(true);

  const handleConnectReddit = () => {
    // start OAuth flow
    window.location.href = `${API_BASE}/auth/reddit/login`;
  };

  const handleCheckSubscribed = async () => {
    setLoading(true);
    try {
      if (!connected) {
        const rMe = await fetch(`${API_BASE}/api/reddit/me`, {
          credentials: "include",
        });
        const jMe = await rMe.json();
        if (jMe.ok) {
          setConnected(true);
          setMe(jMe.me);
        } else {
          toast.info("Please connect to Reddit first.");
          setLoading(false);
          return;
        }
      }

      const r = await fetch(
        `${API_BASE}/api/reddit/check-subreddit?user=${encodeURIComponent(
          user?._id
        )}&name=${encodeURIComponent(SUBREDDIT_NAME)}`,
        { credentials: "include" }
      );
      const j = await r.json();

      if (!j.ok) {
        toast.error(j.error || "Error checking Reddit membership.");
        setLoading(false);
        return;
      }

      if (j.subscribed) {
        toast.success("You are subscribed to the subreddit ✅");
        getData && getData();
        setModalOpen(false);
      } else {
        toast.warn("You are not subscribed yet. Please join and try again.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`${
        user?.reddit && "order-last opacity-40"
      } flex justify-between gap-3 items-center relative border-b py-2 pb-4 text-white`}
    >
      <span className="flex items-center gap-2 lg:gap-4">
        <FaReddit className="text-[32px] sm:text-[54px] lg:text-[72px]" />
        <span>
          <h4 className="font-bold text-[14px] lg:text-[28px] lg:-mb-2 flex items-center gap-2">
            Follow Reddit{" "}
            <FaInfoCircle
              className="text-blue-400 cursor-pointer"
              onClick={() => setIsHelpOpen(true)}
              title="Help"
            />
          </h4>
          <span className="font-bold text-[16px] lg:text-[28px] flex gap-1 items-center">
            <img src="/gold.png" className="w-6 lg:w-8" alt="GOLD" />+{reward}
          </span>
        </span>
      </span>

      {user?.reddit ? (
        <button
          disabled
          className="px-4 lg:px-8 py-2 bg-[#02006A] shadow shadow-white/40 rounded-full text-[14px] lg:text-[24px] flex gap-2 items-center"
        >
          <FaCheck color="green" /> Claimed
        </button>
      ) : (
        <div className="flex flex-col items-end gap-2">
          <div className="flex gap-2">
            <button
              onClick={handleStartClick}
              className="px-4 lg:px-8 py-2 bg-[#02006A] shadow shadow-white/40 rounded-full text-[14px] lg:text-[24px]"
            >
              Start
            </button>
            <button
              onClick={handleOpenModal}
              className="px-4 lg:px-8 py-2 bg-[#02006A] shadow shadow-white/40 rounded-full text-[14px] lg:text-[24px]"
            >
              Check
            </button>
          </div>
        </div>
      )}

      {/* Modal for Connect + Check */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-[#1e293b] p-6 rounded-xl w-full max-w-md text-white shadow-xl">
            <div className="flex justify-end">
              <button onClick={() => setModalOpen(false)}>
                <FaTimes />
              </button>
            </div>
            <h3 className="text-xl font-semibold mb-4 flex items-center justify-between">
              <span>Reddit Verification</span>
              {connected && me?.name && (
                <span className="text-sm opacity-80">
                  Connected as <b>{me.name}</b>
                </span>
              )}
            </h3>

            {!connected ? (
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm opacity-90">
                  You need to connect your Reddit account first.
                </p>
                <button
                  onClick={handleConnectReddit}
                  className="px-4 py-2 rounded bg-orange-600 hover:bg-orange-700"
                >
                  Connect Reddit
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm opacity-90">
                  1) Click <b>Start</b> and join <b>r/{SUBREDDIT_NAME}</b>.
                  <br />
                  2) Then click <b>Check Membership</b> to verify.
                </p>
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => setModalOpen(false)}
                    className="px-4 py-2 rounded bg-gray-600 hover:bg-gray-700"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCheckSubscribed}
                    disabled={loading}
                    className="px-4 py-2 rounded bg-blue-600 hover:bg-blue-700"
                  >
                    {loading ? "Checking..." : "Check Membership"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Help Modal */}
      {isHelpOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50">
          <div className="bg-[#1e293b] rounded-xl max-w-[90%] w-full sm:w-[420px] p-4">
            <div className="flex justify-between items-center mb-4 text-white">
              <h2 className="text-xl font-bold">How This Task Works</h2>
              <button
                onClick={() => setIsHelpOpen(false)}
                className="text-lg font-bold"
              >
                <FaTimes />
              </button>
            </div>
            <p className="text-gray-100 text-left leading-7">
              1) Click <b>Start</b> and go to <b>r/{SUBREDDIT_NAME}</b>.<br />
              2) Join the subreddit.
              <br />
              3) Click <b>Check</b>. If you are not connected, click{" "}
              <b>Connect Reddit</b> first.
              <br />
              4) If your subscription is confirmed, the reward will be added to
              your account automatically.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default RedditTask;
